import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { useReducedMotion } from '../lib/useReducedMotion';
import { playSound } from '../audio/sound';

export interface CoinCounterProps {
  value: number;
  className?: string;
  /** Тривалість «набігання» лічильника, мс. */
  durationMs?: number;
  /** Дзвеніти монетою на кожному кроці. */
  withSound?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

/**
 * Лічильник, що набігає. Класика аренних ігор: цифра не зʼявляється,
 * а «накручується» — і саме цей момент дитина запамʼятовує.
 * При prefers-reduced-motion одразу показуємо кінцеве число.
 */
export function CoinCounter({
  value,
  className,
  durationMs = 900,
  withSound = false,
  prefix,
  suffix,
}: CoinCounterProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced ? value : 0);
  const fromRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reduced) {
      setShown(value);
      return;
    }
    const from = fromRef.current;
    const delta = value - from;
    if (delta === 0) {
      setShown(value);
      return;
    }

    const start = performance.now();
    let lastPlayed = from;

    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic — швидко на старті, мʼяко в кінці.
      const eased = 1 - (1 - t) ** 3;
      const next = Math.round(from + delta * eased);
      setShown(next);
      if (withSound && next !== lastPlayed && next % 2 === 0) {
        lastPlayed = next;
        playSound('coin');
      }
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = value;
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      fromRef.current = value;
    };
  }, [value, durationMs, reduced, withSound]);

  return (
    <span className={cn('tabular inline-flex items-baseline gap-1 font-black', className)}>
      {prefix}
      <span aria-live="polite">{shown}</span>
      {suffix}
    </span>
  );
}
